/**
 * Session bootstrap: who is signed in, which persona is being demonstrated,
 * and where that persona's home is. The persona shown is the session's
 * persona overridden by the acting-as value the server honoured; the shell
 * never trusts sessionStorage alone.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { toast } from 'sonner';
import { ApiError, api, readActingAs, writeActingAs } from './api';
import type { Me, Persona } from './types';

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type Session = {
  status: SessionStatus;
  me: Me | null;
  /** The acting-as value this tab requested (may not be honoured). */
  requestedActingAs: string | null;
  setActingAs: (value: string | null) => void;
  refresh: () => Promise<unknown>;
};

const SessionContext = createContext<Session | null>(null);

export const PERSONA_HOME: Record<Persona, string> = { admin: '/pipeline', qa: '/qa', doctor: '/dr' };
export const PERSONA_LABEL: Record<Persona, string> = { admin: 'Admin', qa: 'QA', doctor: 'Doctor' };

export function personaHome(persona: Persona | undefined): string {
  return persona ? PERSONA_HOME[persona] : '/pipeline';
}

/** The server-resolved name when it exists (a doctor by registry name), else the code as words. */
export function actingAsDisplay(name: string | null | undefined, code: string | null): string | null {
  return name ?? actingAsLabel(code);
}

/** "qa" or "doctor:<slug>" as words; the fallback when no resolved name exists. */
export function actingAsLabel(value: string | null): string | null {
  if (!value) return null;
  if (value === 'qa') return 'QA';
  if (value === 'admin') return 'Admin';
  if (value.startsWith('doctor:')) return `Dr ${value.slice('doctor:'.length)}`;
  return value;
}

/** The probe answers 200 either way; an anonymous visitor is a state, not an error. */
async function fetchMe(): Promise<Me | null> {
  try {
    const res = await api<({ authenticated: true } & Me) | { authenticated: false }>('/api/me', { silent401: true });
    return res.authenticated ? res : null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const query = useQuery({ queryKey: ['me'], queryFn: fetchMe, staleTime: 60_000, retry: 1 });
  const requestedActingAs = readActingAs();

  // A requested acting-as the server did not honour is switched off with a
  // toast: a non-presenter session or the flag off in this environment.
  useEffect(() => {
    if (query.data && requestedActingAs && query.data.actingAs === null) {
      writeActingAs(null);
      toast('Acting as is off in this environment');
      void client.invalidateQueries();
    }
  }, [query.data, requestedActingAs, client]);

  const setActingAs = useCallback((value: string | null) => {
    writeActingAs(value);
    void client.invalidateQueries();
  }, [client]);

  const value = useMemo<Session>(() => ({
    status: query.isPending ? 'loading' : query.data ? 'authenticated' : 'unauthenticated',
    me: query.data ?? null,
    requestedActingAs,
    setActingAs,
    refresh: () => client.invalidateQueries({ queryKey: ['me'] }),
  }), [query.isPending, query.data, requestedActingAs, setActingAs, client]);

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): Session {
  const ctx = use(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/**
 * `?as=qa` or `?as=doctor:<slug>` on first load is written to sessionStorage
 * and stripped from the URL before the router reads it (docs/PLAN.md Part 5,
 * acting-as override).
 */
export function captureActingAsFromUrl(): void {
  const url = new URL(window.location.href);
  const as = url.searchParams.get('as');
  if (!as) return;
  writeActingAs(as);
  url.searchParams.delete('as');
  window.history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash);
}
